import { type ReactElement } from 'react';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const DEFAULT_TEXT_LINE_CLASSES = ['h-5 w-1/2', 'h-5 w-2/3', 'h-5 w-1/3'] as const;

/**
 * Vertically stacked identical skeleton bars (e.g. list or table body loading).
 * `count` is the number of bars; optional `barClassName` sets height and shape.
 */
export function UniformSkeletonStack({
  count,
  ariaLabel,
  barClassName = 'h-9 w-full rounded',
  className,
}: {
  readonly count: number;
  readonly ariaLabel: string;
  readonly barClassName?: string;
  readonly className?: string;
}): ReactElement {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={ariaLabel}
      className={cn('space-y-2', className)}
    >
      {Array.from({ length: count }, (_, idx) => (
        <Skeleton key={idx} className={barClassName} />
      ))}
    </div>
  );
}

/**
 * A short block of variable-width “text line” skeletons (e.g. card body
 * loading). Not for uniform table-row placeholders — use `UniformSkeletonStack`.
 */
export function MultilineTextSkeleton({
  ariaLabel,
  lineClassNames = [...DEFAULT_TEXT_LINE_CLASSES],
  className,
}: {
  readonly ariaLabel: string;
  readonly lineClassNames?: readonly string[];
  readonly className?: string;
}): ReactElement {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      aria-label={ariaLabel}
      className={className}
    >
      <div className="space-y-3">
        {lineClassNames.map((cls, idx) => (
          <Skeleton key={idx} className={cls} />
        ))}
      </div>
    </div>
  );
}
